import React, { useEffect, useState } from 'react';
import { Receipt, Download } from 'lucide-react';
import { listPortalReceipts } from '../../lib/db';
import { formatDate, formatMoney } from '../../lib/formatters';
import { printReceipt } from '../../lib/printReceipt';
import { usePortal } from '../../contexts/PortalContext';

const METHOD_LABELS = {
  cash: 'Cash', bank_transfer: 'Bank Transfer', cheque: 'Cheque',
  mobile_money: 'Mobile Money', card: 'Card', other: 'Other',
};

const SummaryCard = ({ label, value, tone }) => (
  <div className={`rounded-2xl border p-4 ${tone}`}>
    <p className="text-xs font-medium uppercase tracking-wide opacity-70 mb-1">{label}</p>
    <p className="text-2xl font-bold">{value}</p>
  </div>
);

export default function PortalReceiptsPage() {
  const { contact, firm } = usePortal();
  const [receipts, setReceipts] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    listPortalReceipts().then(({ data }) => {
      setReceipts(data || []);
      setLoading(false);
    });
  }, []);

  const totalPaid = receipts.reduce((s, r) => s + Number(r.amount || 0), 0);

  const handleDownload = (r) => {
    printReceipt({
      receiptNumber:  r.receipt_number,
      firm:           firm || {},
      client:         contact || {},
      matterTitle:    r.matter?.title || '',
      amount:         Number(r.amount),
      payment_date:   r.payment_date,
      method:         r.method,
      payment_label:  r.payment_label || '',
      notes:          r.notes || '',
      linkedCharges:  [],
      linkedInvoices: [],
    });
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div>
          <div className="h-8 w-1/3 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
          <div className="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
              <div className="h-3 w-24 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
              <div className="h-7 w-20 bg-slate-200 dark:bg-slate-700 rounded" />
            </div>
          ))}
        </div>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 p-4 border-b border-slate-100 dark:border-slate-800 last:border-0">
              <div className="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
              <div className="h-4 w-32 bg-slate-200 dark:bg-slate-700 rounded" />
              <div className="ml-auto h-4 w-20 bg-slate-200 dark:bg-slate-700 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Receipts</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          {receipts.length} receipt{receipts.length !== 1 ? 's' : ''} · all payments confirmed
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-4">
        <SummaryCard
          label="Total payments made"
          value={receipts.length}
          tone="bg-indigo-50 border-indigo-200 text-indigo-900 dark:bg-indigo-900/20 dark:border-indigo-800 dark:text-indigo-200"
        />
        <SummaryCard
          label="Total amount paid"
          value={formatMoney(totalPaid)}
          tone="bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-200"
        />
      </div>

      {receipts.length === 0 ? (
        <div className="text-center py-20 text-slate-400">
          <Receipt size={40} className="mx-auto mb-4 opacity-30" />
          <p className="font-medium">No receipts yet.</p>
          <p className="text-sm mt-1">Receipts appear here once your firm records a payment.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/40">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">Receipt #</th>
                  <th className="text-left px-5 py-3 font-medium">Case File</th>
                  <th className="text-left px-5 py-3 font-medium">Date</th>
                  <th className="text-left px-5 py-3 font-medium">Method</th>
                  <th className="text-right px-5 py-3 font-medium">Amount</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {receipts.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition group">
                    <td className="px-5 py-3 font-mono text-sm font-medium text-slate-900 dark:text-white">
                      {r.receipt_number}
                    </td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
                      {r.matter?.title || '—'}
                    </td>
                    <td className="px-5 py-3 text-slate-500">{formatDate(r.payment_date)}</td>
                    <td className="px-5 py-3">
                      <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                        {METHOD_LABELS[r.method] || r.method}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right font-semibold text-emerald-600 dark:text-emerald-400 font-mono">
                      {formatMoney(r.amount)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => handleDownload(r)}
                        title="Download PDF"
                        className="opacity-0 group-hover:opacity-100 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium transition"
                      >
                        <Download size={12} /> PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
            {receipts.map((r) => (
              <div key={r.id} className="px-4 py-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-mono font-semibold text-slate-900 dark:text-white truncate">{r.receipt_number}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{r.matter?.title || '—'} · {formatDate(r.payment_date)}</p>
                  <p className="text-xs text-slate-400">{METHOD_LABELS[r.method] || r.method}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-sm font-bold font-mono text-emerald-600 dark:text-emerald-400">{formatMoney(r.amount)}</span>
                  <button
                    onClick={() => handleDownload(r)}
                    className="p-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition"
                  >
                    <Download size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
